import { useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import {
  BadgeCheck,
  Building2,
  Compass,
  CreditCard,
  GitPullRequestArrow,
  Layers3,
} from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { OnboardingTour } from "@/components/OnboardingTour";
import { TrialBanner } from "@/components/TrialBanner";
import { UpgradeModal } from "@/components/UpgradeModal";
import { ReferralTile } from "@/components/ReferralTile";
import { useAuth } from "@/contexts/AuthContext";
import InventoryPage from "@/pages/InventoryPage";
import RequirementsPage from "@/pages/RequirementsPage";
import MatchesPage from "@/pages/MatchesPage";
import PipelinePage from "@/pages/PipelinePage";
import BillingPage from "@/pages/BillingPage";

function DashboardHome() {
  const { user } = useAuth();
  const firstName = user?.full_name?.split(" ")[0] || "there";

  const cards = [
    {
      to: "inventory",
      label: "Inventory",
      icon: Building2,
      description: "List and manage your active properties with exclusive ATS.",
      testId: "card-inventory",
    },
    {
      to: "requirements",
      label: "Requirements",
      icon: Compass,
      description: "Publish buyer requirements so the network can source for you.",
      testId: "card-requirements",
    },
    {
      to: "matches",
      label: "Matches",
      icon: Layers3,
      description: "AI-scored matches between inventory and live requirements.",
      testId: "card-matches",
    },
    {
      to: "pipeline",
      label: "Pipeline",
      icon: GitPullRequestArrow,
      description: "Track co-broke deals through a shared Kanban pipeline.",
      testId: "card-pipeline",
    },
    {
      to: "billing",
      label: "Billing",
      icon: CreditCard,
      description:
        "Trial status, subscription tiers, and PayMongo transaction history.",
      testId: "card-billing",
    },
  ];

  return (
    <section className="mx-auto w-full max-w-7xl px-6 py-12 md:px-10 md:py-16 mb-rise">
      <div className="flex flex-col gap-2">
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-brand-green/25 bg-brand-green/10 px-3 py-1 text-xs font-semibold text-brand-green">
          <BadgeCheck className="h-3.5 w-3.5" />
          Verified broker
        </span>
        <h1 className="mt-4 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          Good to see you, {firstName}.
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-gray-500">
          This is your MatchBroker workspace. Inventory, matches, and co-broke
          deals are ready to go — pick any tile to jump in.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
        {cards.map((c) => (
          <a
            key={c.to}
            href={`/dashboard/${c.to}`}
            className="group mb-transition rounded-2xl border border-gray-100 bg-white p-8 shadow-mb-card hover:-translate-y-[1px] hover:border-gray-200 hover:shadow-mb-card-hover"
            data-testid={c.testId}
          >
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-navy text-white">
                <c.icon className="h-5 w-5" />
              </span>
              <div className="text-lg font-semibold text-gray-900">{c.label}</div>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-gray-500">
              {c.description}
            </p>
            <div className="mt-6 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-navy">
              Open
              <span className="mb-transition group-hover:translate-x-0.5">→</span>
            </div>
          </a>
        ))}
      </div>

      <div className="mt-16">
        <ReferralTile />
      </div>
    </section>
  );
}

function DashboardShell() {
  const location = useLocation();
  const { user } = useAuth();
  const [tourOpen, setTourOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Trigger the onboarding tour once, on first arrival to the dashboard,
  // for verified brokers whose onboarding_completed is still false.
  useEffect(() => {
    if (!user) return;
    if (user.role !== "broker") return;
    if (!user.is_verified) return;
    if (user.onboarding_completed) return;
    // Small delay so the app-header / nav has painted before spotlighting.
    const t = setTimeout(() => setTourOpen(true), 400);
    return () => clearTimeout(t);
  }, [user]);

  return (
    <div className="mb-page flex min-h-screen flex-col" data-testid="broker-dashboard">
      <AppHeader />
      <TrialBanner trial={user?.trial} onUpgrade={() => setUpgradeOpen(true)} />
      <main key={location.pathname} className="flex-1">
        <Outlet />
      </main>
      <OnboardingTour open={tourOpen} onClose={() => setTourOpen(false)} />
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </div>
  );
}

export default function BrokerDashboard() {
  return (
    <Routes>
      <Route element={<DashboardShell />}>
        <Route index element={<DashboardHome />} />
        <Route
          path="inventory"
          element={<InventoryPage />}
        />
        <Route
          path="requirements"
          element={<RequirementsPage />}
        />
        <Route
          path="matches"
          element={<MatchesPage />}
        />
        <Route
          path="pipeline"
          element={<PipelinePage />}
        />
        <Route
          path="billing"
          element={<BillingPage />}
        />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
